import React, { useEffect, useRef, useState } from 'react';
import {
  UserIcon,
  LockClosedIcon,
  CameraIcon,
  CheckIcon,
  EyeIcon,
  EyeSlashIcon,
  CreditCardIcon,
  PencilIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../lib/apiClient';

interface Profile {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar_url: string | null;
}

interface GatewaySetting {
  id: string;
  gateway: string;
  mode: 'live' | 'test';
  live_key_id: string;
  live_key_secret: string;
  test_key_id: string;
  test_key_secret: string;
  is_active: boolean;
}

interface ApiSetting {
  id: string;
  api_name: string;
  api_key: string;
  model_name: string;
  is_active: boolean;
  description: string;
}

interface SerperTemplate {
  id: string;
  name: string;
  template: string;
  is_default: boolean;
  is_active: boolean;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-2.5 ml-1">
      {children}
    </label>
  );
}

function TextInput({ ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full border text-[14px] font-bold px-4 py-3.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all ${
        props.readOnly
          ? 'bg-slate-100/80 border-slate-200 text-slate-400 cursor-not-allowed'
          : 'bg-white border-slate-200 text-slate-900'
      } ${props.className ?? ''}`}
    />
  );
}

function Toast({ message, type }: { message: string; type: 'success' | 'error' }) {
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-[13px] font-bold transition-all ${
      type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
    }`}>
      {type === 'success' && <CheckIcon className="w-4 h-4 shrink-0" />}
      {message}
    </div>
  );
}

export default function AdminSettings() {
  // ── Profile state ──────────────────────────────────────────────────────────
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // ── Avatar state ───────────────────────────────────────────────────────────
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // ── Change email state ─────────────────────────────────────────────────────
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');

  // ── Password state ─────────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // ── Toast ──────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // ── Gateway state ──────────────────────────────────────────────────────────
  const [gateways, setGateways] = useState<GatewaySetting[]>([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [showGatewayModal, setShowGatewayModal] = useState(false);
  const [editingGateway, setEditingGateway] = useState<GatewaySetting | null>(null);
  const [gatewayForm, setGatewayForm] = useState({
    mode: 'test' as 'live' | 'test',
    live_key_id: '',
    live_key_secret: '',
    test_key_id: '',
    test_key_secret: '',
  });
  const [savingGateway, setSavingGateway] = useState(false);
  const [activatingGatewayId, setActivatingGatewayId] = useState<string | null>(null);
  const [showLiveSecret, setShowLiveSecret] = useState(false);
  const [showTestSecret, setShowTestSecret] = useState(false);

  // ── API Settings state ─────────────────────────────────────────────────────
  const [apiSettings, setApiSettings] = useState<ApiSetting[]>([]);
  const [apiSettingsLoading, setApiSettingsLoading] = useState(false);
  const [showApiModal, setShowApiModal] = useState(false);
  const [editingApiSetting, setEditingApiSetting] = useState<ApiSetting | null>(null);
  const [apiSettingForm, setApiSettingForm] = useState({
    api_key: '',
    model_name: '',
  });
  const [savingApiSetting, setSavingApiSetting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // ── Serper Templates state ─────────────────────────────────────────────────
  const [templates, setTemplates] = useState<SerperTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SerperTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    template: '',
  });
  const [savingTemplate, setSavingTemplate] = useState(false);

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Load profile ───────────────────────────────────────────────────────────
  useEffect(() => {
    apiClient.get('/admin/profile')
      .then(({ data }) => {
        setProfile(data);
        setName(data.name);
        setEmail(data.email);
        setProfileLoading(false);
      })
      .catch(() => setProfileLoading(false));
  }, []);

  // ── Load gateways ──────────────────────────────────────────────────────────
  const loadGateways = () => {
    setGatewaysLoading(true);
    apiClient.get('/admin/settings/gateway')
      .then(({ data }) => {
        setGateways(Array.isArray(data) ? data : []);
      })
      .catch(() => showToast('Failed to load payment gateways.', 'error'))
      .finally(() => setGatewaysLoading(false));
  };

  // ── Load API settings ──────────────────────────────────────────────────────
  const loadApiSettings = () => {
    setApiSettingsLoading(true);
    apiClient.get('/admin/settings/api')
      .then(({ data }) => {
        setApiSettings(Array.isArray(data) ? data : []);
      })
      .catch(() => showToast('Failed to load API settings.', 'error'))
      .finally(() => setApiSettingsLoading(false));
  };

  useEffect(() => {
    loadGateways();
    loadApiSettings();
  }, []);

  // ── Avatar picker ──────────────────────────────────────────────────────────
  const handleAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    setUploadingAvatar(true);
    const form = new FormData();
    form.append('avatar', avatarFile);
    try {
      const { data } = await apiClient.post('/admin/profile/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfile(p => p ? { ...p, avatar_url: data.avatar_url } : p);
      setAvatarFile(null);
      showToast('Avatar updated successfully.', 'success');
    } catch {
      showToast('Failed to upload avatar.', 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // ── Save profile ───────────────────────────────────────────────────────────
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await apiClient.put('/admin/profile', { name });
      setProfile(p => p ? { ...p, name: data.name } : p);
      showToast('Profile updated successfully.', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message ?? 'Failed to save profile.', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Change email (requires password) ─────────────────────────────────────────
  const handleChangeEmail = async () => {
    setEmailError('');
    const trimmed = newEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (trimmed.toLowerCase() === (profile?.email ?? '').toLowerCase()) {
      setEmailError('That is already your current email.');
      return;
    }
    if (!emailPassword) {
      setEmailError('Enter your password to confirm.');
      return;
    }
    setSavingEmail(true);
    try {
      const { data } = await apiClient.post('/admin/profile/email', {
        email: trimmed,
        password: emailPassword,
      });
      setProfile(p => p ? { ...p, email: data.email } : p);
      setEmail(data.email);
      setNewEmail('');
      setEmailPassword('');
      showToast('Email updated successfully.', 'success');
    } catch (err: any) {
      setEmailError(err.response?.data?.message ?? 'Failed to update email.');
    } finally {
      setSavingEmail(false);
    }
  };

  // ── Change password ────────────────────────────────────────────────────────
  const handleChangePassword = async () => {
    setPasswordError('');
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    setSavingPassword(true);
    try {
      await apiClient.post('/admin/profile/password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast('Password changed successfully.', 'success');
    } catch (err: any) {
      setPasswordError(err.response?.data?.message ?? 'Failed to change password.');
    } finally {
      setSavingPassword(false);
    }
  };

  // ── Gateway modal ──────────────────────────────────────────────────────────
  const handleOpenGatewayModal = (gateway: GatewaySetting | null) => {
    if (gateway) {
      setEditingGateway(gateway);
      setGatewayForm({
        mode: gateway.mode,
        live_key_id: gateway.live_key_id,
        live_key_secret: gateway.live_key_secret,
        test_key_id: gateway.test_key_id,
        test_key_secret: gateway.test_key_secret,
      });
    } else {
      setEditingGateway(null);
    }
    setShowGatewayModal(true);
  };

  const handleSaveGateway = async () => {
    if (!editingGateway) return;
    setSavingGateway(true);
    try {
      await apiClient.put(`/admin/settings/gateway/${editingGateway.id}`, gatewayForm);
      setShowGatewayModal(false);
      loadGateways();
      showToast('Payment gateway settings updated successfully.', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message ?? 'Failed to update gateway settings.', 'error');
    } finally {
      setSavingGateway(false);
    }
  };

  const handleSetActiveGateway = async (gateway: GatewaySetting) => {
    setActivatingGatewayId(gateway.id);
    try {
      await apiClient.put(`/admin/settings/gateway/${gateway.id}/active`);
      loadGateways();
      showToast(`${gateway.gateway.toUpperCase()} is now the active payment gateway.`, 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message ?? 'Failed to switch gateway.', 'error');
    } finally {
      setActivatingGatewayId(null);
    }
  };

  // ── API Settings handlers ──────────────────────────────────────────────────
  const handleOpenApiModal = (setting: ApiSetting | null) => {
    if (setting) {
      setEditingApiSetting(setting);
      setApiSettingForm({
        api_key: '',
        model_name: setting.model_name,
      });
    } else {
      setEditingApiSetting(null);
    }
    setShowApiModal(true);
  };

  const handleSaveApiSetting = async () => {
    if (!editingApiSetting) return;
    setSavingApiSetting(true);
    try {
      await apiClient.put(`/admin/settings/api/${editingApiSetting.id}`, apiSettingForm);
      setShowApiModal(false);
      loadApiSettings();
      showToast('API settings updated successfully.', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message ?? 'Failed to update API settings.', 'error');
    } finally {
      setSavingApiSetting(false);
    }
  };

  // ── Load Serper Templates ────────────────────────────────────────────────
  const loadTemplates = () => {
    setTemplatesLoading(true);
    apiClient.get('/admin/serper-templates')
      .then(({ data }) => {
        // Handle paginated response
        const templateList = Array.isArray(data) ? data : (data.data || []);
        setTemplates(templateList);
      })
      .catch(() => showToast('Failed to load templates', 'error'))
      .finally(() => setTemplatesLoading(false));
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleOpenTemplateModal = (template?: SerperTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({ name: template.name, template: template.template });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ name: '', template: '' });
    }
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.template.trim()) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        await apiClient.put(`/admin/serper-templates/${editingTemplate.id}`, templateForm);
        showToast('Template updated', 'success');
      } else {
        await apiClient.post('/admin/serper-templates', templateForm);
        showToast('Template created', 'success');
      }
      setShowTemplateModal(false);
      loadTemplates();
    } catch (e: any) {
      showToast(e.response?.data?.message || 'Error saving template', 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSetDefaultTemplate = async (id: string) => {
    try {
      await apiClient.patch(`/admin/serper-templates/${id}/set-default`);
      showToast('Default template updated', 'success');
      loadTemplates();
    } catch (e: any) {
      showToast(e.response?.data?.message || 'Error updating template', 'error');
    }
  };

  const currentAvatar = avatarPreview ?? profile?.avatar_url;
  const initials = profile ? profile.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'AD';

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[900px] mx-auto flex flex-col gap-6 pb-20">

      {/* Header */}
      <div>
        <h1 className="text-[28px] font-extrabold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-[13px] text-slate-500 mt-1">Manage your admin profile and security credentials.</p>
      </div>

      {/* ── Admin Profile Card ─────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-brand-50 text-brand-600 rounded-xl flex items-center justify-center shrink-0">
            <UserIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-[16px] font-extrabold text-slate-900">Admin Profile</h2>
            <p className="text-[12px] text-slate-400 mt-0.5">Update your display name, email, and photo.</p>
          </div>
        </div>

        {profileLoading ? (
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
            <div className="w-28 h-28 rounded-2xl bg-slate-100 animate-pulse shrink-0" />
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="space-y-2">
                  <div className="h-3 bg-slate-100 animate-pulse rounded w-24" />
                  <div className="h-12 bg-slate-100 animate-pulse rounded-xl" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-8">

            {/* Avatar */}
            <div className="flex flex-col items-center gap-3 shrink-0">
              <div className="relative group">
                <div className="w-28 h-28 rounded-2xl overflow-hidden bg-brand-500 flex items-center justify-center shadow-sm">
                  {currentAvatar ? (
                    <img src={currentAvatar} alt="avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[28px] font-black text-white">{initials}</span>
                  )}
                </div>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="absolute inset-0 rounded-2xl flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <CameraIcon className="w-7 h-7 text-white" />
                </button>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg,image/webp"
                className="hidden"
                onChange={handleAvatarPick}
              />
              {avatarFile ? (
                <button
                  onClick={handleAvatarUpload}
                  disabled={uploadingAvatar}
                  className="text-[12px] font-bold bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white px-4 py-1.5 rounded-lg transition-colors"
                >
                  {uploadingAvatar ? 'Uploading…' : 'Save Photo'}
                </button>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="text-[12px] font-bold text-brand-600 hover:text-slate-700 transition-colors"
                >
                  Change Photo
                </button>
              )}
              <p className="text-[10px] text-slate-400 text-center">JPG, PNG, WEBP · max 2MB</p>
            </div>

            {/* Fields */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
              <div>
                <FieldLabel>Full Name</FieldLabel>
                <TextInput
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Admin name"
                />
              </div>
              <div>
                <FieldLabel>Email Address</FieldLabel>
                <TextInput type="email" value={email} readOnly />
                <p className="text-[11px] text-slate-400 mt-1.5 ml-1">Change your email below — password required.</p>
              </div>
              <div>
                <FieldLabel>Admin Role</FieldLabel>
                <TextInput type="text" value={profile?.role ?? 'admin'} readOnly />
              </div>
              <div>
                <FieldLabel>Account ID</FieldLabel>
                <TextInput type="text" value={profile?.id ?? '—'} readOnly />
              </div>
              <div className="md:col-span-2 flex justify-end pt-1">
                <button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl text-[13px] font-bold transition-colors shadow-sm shadow-brand-500/20"
                >
                  {savingProfile ? 'Saving…' : 'Save Profile'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Change Email Card ──────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-brand-50 text-brand-600 rounded-xl flex items-center justify-center shrink-0">
            <EnvelopeIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-[16px] font-extrabold text-slate-900">Change Email</h2>
            <p className="text-[12px] text-slate-400 mt-0.5">Confirm with your password to update the email you sign in with.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <FieldLabel>New Email Address</FieldLabel>
            <TextInput
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              placeholder="new-admin@example.com"
              autoComplete="off"
            />
          </div>
          <div>
            <FieldLabel>Current Password</FieldLabel>
            <div className="relative">
              <input
                type={showEmailPassword ? 'text' : 'password'}
                value={emailPassword}
                onChange={e => setEmailPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="off"
                className="w-full border border-slate-200 bg-white text-slate-900 text-[14px] font-bold px-4 py-3.5 pr-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowEmailPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showEmailPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {emailError && (
          <p className="mt-3 text-[12px] font-semibold text-red-600">{emailError}</p>
        )}

        <div className="flex justify-end mt-5">
          <button
            onClick={handleChangeEmail}
            disabled={savingEmail || !newEmail.trim() || !emailPassword}
            className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-xl text-[13px] font-bold transition-colors shadow-sm shadow-brand-500/20"
          >
            {savingEmail ? 'Updating…' : 'Update Email'}
          </button>
        </div>
      </div>

      {/* ── Security Card ──────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-brand-50 text-brand-600 rounded-xl flex items-center justify-center shrink-0">
            <LockClosedIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-[16px] font-extrabold text-slate-900">Change Password</h2>
            <p className="text-[12px] text-slate-400 mt-0.5">Use a strong password of at least 8 characters.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { label: 'Current Password', value: currentPassword, show: showCurrent, setShow: setShowCurrent, onChange: setCurrentPassword, placeholder: '••••••••' },
            { label: 'New Password',     value: newPassword,     show: showNew,     setShow: setShowNew,     onChange: setNewPassword,     placeholder: 'Min. 8 characters' },
            { label: 'Confirm Password', value: confirmPassword, show: showConfirm, setShow: setShowConfirm, onChange: setConfirmPassword, placeholder: 'Repeat new password' },
          ].map(field => (
            <div key={field.label}>
              <FieldLabel>{field.label}</FieldLabel>
              <div className="relative">
                <input
                  type={field.show ? 'text' : 'password'}
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full border border-slate-200 bg-white text-slate-900 text-[14px] font-bold px-4 py-3.5 pr-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all"
                />
                <button
                  type="button"
                  onClick={() => field.setShow((v: boolean) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {field.show ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>

        {passwordError && (
          <p className="mt-3 text-[12px] font-semibold text-red-600">{passwordError}</p>
        )}

        {/* Password strength indicator */}
        {newPassword && (
          <div className="mt-4 flex items-center gap-3">
            <div className="flex gap-1">
              {[1, 2, 3, 4].map(level => {
                const strength = newPassword.length >= 12 && /[A-Z]/.test(newPassword) && /\d/.test(newPassword) && /[^A-Za-z0-9]/.test(newPassword) ? 4
                  : newPassword.length >= 10 && /[A-Z]/.test(newPassword) && /\d/.test(newPassword) ? 3
                  : newPassword.length >= 8 ? 2 : 1;
                return (
                  <div key={level} className={`h-1.5 w-10 rounded-full transition-colors ${
                    level <= strength
                      ? strength === 4 ? 'bg-emerald-500' : strength === 3 ? 'bg-brand-500' : strength === 2 ? 'bg-amber-400' : 'bg-red-400'
                      : 'bg-slate-200'
                  }`} />
                );
              })}
            </div>
            <span className="text-[11px] font-semibold text-slate-400">
              {newPassword.length < 8 ? 'Too short' : newPassword.length >= 12 && /[A-Z]/.test(newPassword) && /\d/.test(newPassword) && /[^A-Za-z0-9]/.test(newPassword) ? 'Strong' : newPassword.length >= 10 && /[A-Z]/.test(newPassword) ? 'Good' : 'Fair'}
            </span>
          </div>
        )}

        <div className="flex justify-end mt-5">
          <button
            onClick={handleChangePassword}
            disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword}
            className="flex items-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-xl text-[13px] font-bold transition-colors shadow-sm shadow-brand-500/20"
          >
            {savingPassword ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </div>

      {/* ── Payment Gateway Card ───────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-brand-50 text-brand-600 rounded-xl flex items-center justify-center shrink-0">
            <CreditCardIcon className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-[16px] font-extrabold text-slate-900">Payment Gateway</h2>
            <p className="text-[12px] text-slate-400 mt-0.5">Manage your PayU Merchant Key &amp; Salt and switch between live and test modes.</p>
          </div>
        </div>

        {gatewaysLoading ? (
          <div className="space-y-2">
            {[1, 2].map(i => (
              <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : gateways.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-[13px]">No payment gateways configured.</div>
        ) : (
          <div className="space-y-3">
            {gateways.map(gateway => (
              <div key={gateway.id} className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                gateway.is_active ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-100 hover:border-slate-200'
              }`}>
                <div className="flex items-center gap-4 flex-1">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[13px] font-bold text-slate-900 uppercase">{gateway.gateway}</span>
                      <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                        gateway.mode === 'live' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'
                      }`}>
                        {gateway.mode}
                      </span>
                      {gateway.is_active ? (
                        <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                      ) : (
                        <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">Inactive</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-medium text-slate-500">
                        {gateway.gateway === 'payu' ? 'Live key/salt' : 'Live'}: {gateway.live_key_id ? '✓' : '—'} {gateway.live_key_secret ? '✓' : '—'}
                      </span>
                      <span className="text-[11px] font-medium text-slate-500">
                        {gateway.gateway === 'payu' ? 'Test key/salt' : 'Test'}: {gateway.test_key_id ? '✓' : '—'} {gateway.test_key_secret ? '✓' : '—'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!gateway.is_active && (
                    <button
                      onClick={() => handleSetActiveGateway(gateway)}
                      disabled={activatingGatewayId !== null}
                      className="flex items-center gap-1.5 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-colors"
                    >
                      {activatingGatewayId === gateway.id ? 'Activating…' : 'Set Active'}
                    </button>
                  )}
                  <button
                    onClick={() => handleOpenGatewayModal(gateway)}
                    className="flex items-center gap-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" /> Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Gateway Modal ──────────────────────────────────────────────────── */}
      {showGatewayModal && editingGateway && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-xl">
            <h3 className="text-lg font-extrabold text-slate-900 mb-1">Edit {editingGateway.gateway.toUpperCase()}</h3>
            {editingGateway.gateway === 'payu' && (
              <p className="text-[12px] text-slate-400 mb-4">Enter your PayU <span className="font-semibold">Merchant Key</span> and <span className="font-semibold">Salt (V1)</span> from the PayU dashboard.</p>
            )}

            <div className="space-y-4">
              {/* Mode */}
              <div>
                <FieldLabel>Mode</FieldLabel>
                <div className="flex gap-3">
                  {(['test', 'live'] as const).map(m => (
                    <label key={m} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="mode"
                        value={m}
                        checked={gatewayForm.mode === m}
                        onChange={() => setGatewayForm(f => ({ ...f, mode: m }))}
                        className="w-4 h-4"
                      />
                      <span className="text-[13px] font-semibold text-slate-700 capitalize">{m}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Live Keys */}
              <div className="pt-2 border-t border-slate-200">
                <h4 className="text-[12px] font-extrabold text-slate-900 uppercase mb-3">Live {editingGateway.gateway === 'payu' ? 'Credentials' : 'Keys'}</h4>
                <div className="space-y-3">
                  <div>
                    <FieldLabel>{editingGateway.gateway === 'payu' ? 'Live Merchant Key' : 'Live Key ID'}</FieldLabel>
                    <TextInput
                      type="text"
                      value={gatewayForm.live_key_id}
                      onChange={e => setGatewayForm(f => ({ ...f, live_key_id: e.target.value }))}
                      placeholder="Enter or keep empty to preserve"
                    />
                  </div>
                  <div>
                    <FieldLabel>{editingGateway.gateway === 'payu' ? 'Live Salt' : 'Live Key Secret'}</FieldLabel>
                    <div className="relative">
                      <input
                        type={showLiveSecret ? 'text' : 'password'}
                        value={gatewayForm.live_key_secret}
                        onChange={e => setGatewayForm(f => ({ ...f, live_key_secret: e.target.value }))}
                        placeholder="Enter or keep empty to preserve"
                        className="w-full border border-slate-200 bg-white text-slate-900 text-[14px] font-bold px-4 py-3 pr-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLiveSecret(!showLiveSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showLiveSecret ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Keys */}
              <div className="pt-2 border-t border-slate-200">
                <h4 className="text-[12px] font-extrabold text-slate-900 uppercase mb-3">Test {editingGateway.gateway === 'payu' ? 'Credentials' : 'Keys'}</h4>
                <div className="space-y-3">
                  <div>
                    <FieldLabel>{editingGateway.gateway === 'payu' ? 'Test Merchant Key' : 'Test Key ID'}</FieldLabel>
                    <TextInput
                      type="text"
                      value={gatewayForm.test_key_id}
                      onChange={e => setGatewayForm(f => ({ ...f, test_key_id: e.target.value }))}
                      placeholder="Enter or keep empty to preserve"
                    />
                  </div>
                  <div>
                    <FieldLabel>{editingGateway.gateway === 'payu' ? 'Test Salt' : 'Test Key Secret'}</FieldLabel>
                    <div className="relative">
                      <input
                        type={showTestSecret ? 'text' : 'password'}
                        value={gatewayForm.test_key_secret}
                        onChange={e => setGatewayForm(f => ({ ...f, test_key_secret: e.target.value }))}
                        placeholder="Enter or keep empty to preserve"
                        className="w-full border border-slate-200 bg-white text-slate-900 text-[14px] font-bold px-4 py-3 pr-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowTestSecret(!showTestSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showTestSecret ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
              <button
                onClick={() => setShowGatewayModal(false)}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-colors text-[13px]"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGateway}
                disabled={savingGateway}
                className="flex-1 px-4 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white font-bold rounded-lg transition-colors text-[13px]"
              >
                {savingGateway ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── API Settings Card ────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-brand-50 text-brand-600 rounded-xl flex items-center justify-center shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14m0 0v4m0-4V10m0 0H9a2 2 0 00-2 2v4a2 2 0 002 2h6m0-14H9a2 2 0 00-2 2v4a2 2 0 002 2h6" />
            </svg>
          </div>
          <div>
            <h2 className="text-[16px] font-extrabold text-slate-900">API Settings</h2>
            <p className="text-[12px] text-slate-400 mt-0.5">Manage OpenRouter and Serper API keys.</p>
          </div>
        </div>

        {apiSettingsLoading ? (
          <div className="space-y-2">
            {[1, 2].map(i => (
              <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : apiSettings.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-[13px]">No API settings configured.</div>
        ) : (
          <div className="space-y-3">
            {apiSettings.map(setting => (
              <div key={setting.id} className="flex items-center justify-between p-4 border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
                <div className="flex items-center gap-4 flex-1">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[13px] font-bold text-slate-900 capitalize">{setting.api_name}</span>
                      {setting.is_active && (
                        <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-medium text-slate-500">
                        Key: {setting.api_key ? '●●●●●●●●' : '—'}
                      </span>
                      {setting.model_name && (
                        <span className="text-[11px] font-medium text-slate-500">
                          Model: {setting.model_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleOpenApiModal(setting)}
                  className="flex items-center gap-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200 px-3 py-1.5 rounded-lg text-[12px] font-bold transition-colors shrink-0"
                >
                  <PencilIcon className="w-4 h-4" /> Edit
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── API Settings Modal ─────────────────────────────────────────────── */}
      {showApiModal && editingApiSetting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-xl">
            <h3 className="text-lg font-extrabold text-slate-900 mb-4">Edit API Settings</h3>

            <div className="space-y-4">
              <div>
                <FieldLabel>Service Name</FieldLabel>
                <TextInput
                  type="text"
                  value={editingApiSetting.api_name}
                  readOnly
                  className="bg-slate-100"
                />
              </div>

              <div>
                <FieldLabel>API Key</FieldLabel>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiSettingForm.api_key}
                    onChange={e => setApiSettingForm(f => ({ ...f, api_key: e.target.value }))}
                    placeholder="Leave empty to keep current key"
                    className="w-full border border-slate-200 bg-white text-slate-900 text-[14px] font-bold px-4 py-3 pr-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showApiKey ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {editingApiSetting.api_name === 'openrouter' && (
                <div>
                  <FieldLabel>Model Name</FieldLabel>
                  <TextInput
                    type="text"
                    value={apiSettingForm.model_name}
                    onChange={e => setApiSettingForm(f => ({ ...f, model_name: e.target.value }))}
                    placeholder="e.g., meta-llama/llama-3.3-70b-instruct"
                  />
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6 pt-4 border-t border-slate-200">
              <button
                onClick={() => setShowApiModal(false)}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-colors text-[13px]"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveApiSetting}
                disabled={savingApiSetting}
                className="flex-1 px-4 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white font-bold rounded-lg transition-colors text-[13px]"
              >
                {savingApiSetting ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Serper Query Template */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-extrabold text-slate-900">Serper Query Template</h3>
            <p className="text-xs text-slate-500 mt-1">Customize the query template used for job searches across the platform</p>
          </div>
          <button
            onClick={() => handleOpenTemplateModal(templates.find(t => t.is_default))}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold rounded-lg transition-colors"
          >
            Edit Template
          </button>
        </div>

        {templatesLoading ? (
          <div className="space-y-3">
            <div className="h-16 bg-slate-100 rounded-lg animate-pulse" />
          </div>
        ) : templates.find(t => t.is_default) ? (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="grid grid-cols-3 gap-4 md:grid-cols-4">
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Template Name</p>
                <p className="text-sm font-bold text-slate-900">{templates.find(t => t.is_default)?.name}</p>
              </div>
              <div className="col-span-2 md:col-span-3">
                <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Template String</p>
                <code className="text-xs font-mono bg-white border border-slate-200 px-3 py-2 rounded-lg block overflow-x-auto text-slate-700">
                  {templates.find(t => t.is_default)?.template}
                </code>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-[11px] text-blue-700 font-medium">
                <strong>Available placeholders:</strong> {'{titles}'} (comma-separated job titles), {'{company}'} (company name), {'{location}'} (location)
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-400">No active template configured</p>
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">{editingTemplate ? 'Edit Template' : 'Create Template'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-2">Template Name</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Default, LinkedIn Only"
                  className="w-full border border-slate-200 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-sm"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-2">Template String</label>
                <textarea
                  value={templateForm.template}
                  onChange={(e) => setTemplateForm({ ...templateForm, template: e.target.value })}
                  placeholder='{titles} "{company}" {location}'
                  className="w-full border border-slate-200 px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-sm font-mono h-24 resize-none"
                />
                <p className="text-[11px] text-slate-500 mt-2">Available: {'{titles}'}, {'{company}'}, {'{location}'}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowTemplateModal(false)}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={savingTemplate}
                className="flex-1 px-4 py-2.5 bg-brand-600 text-white font-bold rounded-lg hover:bg-brand-700 disabled:bg-slate-300 transition-colors"
              >
                {savingTemplate ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Database Export */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-base font-extrabold text-slate-900">Database Export</h3>
            <p className="text-xs text-slate-500 mt-1">Export all data in SQL file</p>
          </div>
          <button
            onClick={handleExportDatabase}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors"
          >
            Export Database
          </button>
        </div>
        <p className="text-xs text-slate-600">
          Exports: Users, Payments, Search Tasks, Decision Makers, Plans, Support Tickets, Coupons
        </p>
      </div>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}

function handleExportDatabase() {
  apiClient.get('/admin/export/database', { responseType: 'blob' })
    .then((res) => {
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use default
      const contentDisposition = res.headers['content-disposition'];
      let filename = `founderfinder_export_${new Date().toISOString().split('T')[0]}.sql`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch((e) => {
      console.error('Export failed:', e);
      alert('Failed to export database');
    });
}
